import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import {
  format,
  startOfWeek,
  eachDayOfInterval,
  eachMonthOfInterval,
  isWithinInterval,
  differenceInCalendarDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { BlockCounted } from '../blockCounted';
import { Box } from '@mui/material';
import { PeriodType } from '../periodToggle';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Op = {
  id: number;
  amount: number;
  name: string;
  date: string;
};

interface ChartBarTagProps {
  period: PeriodType;
  type: 'income' | 'expense';
  tagId: number;
  title?: string;
  from?: string;
  to?: string;
}

const pct = (part: number, sum: number) => (sum > 0 ? Math.round((part / sum) * 100) : 0);

export const ChartBarTag: React.FC<ChartBarTagProps> = ({
  period,
  type,
  tagId,
  title,
  from,
  to,
}) => {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      tagId,
    };

    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }

    return params;
  }, [tagId, period, from, to]);

  const { data: ops = [] } = useQuery<Op[]>({
    queryKey: ['tag-ops', type, tagId, period, from, to],
    queryFn: async () => {
      const endpoint = type === 'income' ? '/incomes/by-tag' : '/expenses/by-tag';

      const res = await api.get(endpoint, {
        params: queryParams,
      });

      return res.data;
    },
  });

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const today = now.getDate();

  const computed = useMemo(() => {
    let labels: string[] = [];
    let values: number[] = [];

    const weekStart = startOfWeek(now, { locale: ru, weekStartsOn: 1 });
    const weekDays = eachDayOfInterval({ start: weekStart, end: now });

    if (period === 'years') {
      labels = [String(currentYear - 1), String(currentYear)];
      values = [0, 0];

      ops.forEach(o => {
        const d = new Date(o.date);
        const y = d.getFullYear();

        if (y === currentYear - 1) values[0] += Number(o.amount);
        else if (y === currentYear) values[1] += Number(o.amount);
      });

      const total = values.reduce((a, b) => a + b, 0);
      return { labels, values, total };
    }

    if (period === 'year') {
      labels = Array.from({ length: currentMonth + 1 }, (_, m) =>
        format(new Date(currentYear, m, 1), 'LLLL', { locale: ru }),
      );
      values = new Array(currentMonth + 1).fill(0);

      ops.forEach(o => {
        const d = new Date(o.date);
        if (d.getFullYear() !== currentYear) return;

        const m = d.getMonth();
        if (m <= currentMonth) values[m] += Number(o.amount);
      });

      const total = values.reduce((a, b) => a + b, 0);
      return { labels, values, total };
    }

    if (period === 'month') {
      const weeks = Math.ceil(today / 7);

      labels = Array.from({ length: weeks }, (_, i) => {
        const start = i * 7 + 1;
        const end = Math.min((i + 1) * 7, today);
        return `${start}-${end} ${format(now, 'LLLL', { locale: ru })}`;
      });

      values = new Array(weeks).fill(0);

      ops.forEach(o => {
        const d = new Date(o.date);
        if (d.getFullYear() !== currentYear) return;
        if (d.getMonth() !== currentMonth) return;
        if (d.getDate() > today) return;

        const idx = Math.floor((d.getDate() - 1) / 7);
        if (idx >= 0 && idx < values.length) values[idx] += Number(o.amount);
      });

      const total = values.reduce((a, b) => a + b, 0);
      return { labels, values, total };
    }

    if (period === 'week') {
      labels = weekDays.map(d => format(d, 'EEEEEE', { locale: ru }));
      values = new Array(weekDays.length).fill(0);

      ops.forEach(o => {
        const d = new Date(o.date);
        const idx = weekDays.findIndex(x => x.toDateString() === d.toDateString());
        if (idx >= 0) values[idx] += Number(o.amount);
      });

      const total = values.reduce((a, b) => a + b, 0);
      return { labels, values, total };
    }

    if (period === 'custom' && from && to) {
      const start = new Date(from);
      const end = new Date(to);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
        return { labels: [], values: [], total: 0 };
      }

      end.setHours(23, 59, 59, 999);

      const totalDays = differenceInCalendarDays(end, start) + 1;

      if (totalDays <= 31) {
        const days = eachDayOfInterval({ start, end });

        labels = days.map(d => format(d, 'dd.MM', { locale: ru }));
        values = new Array(days.length).fill(0);

        ops.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;

          const idx = days.findIndex(x => x.toDateString() === d.toDateString());
          if (idx >= 0) values[idx] += Number(o.amount);
        });

        const total = values.reduce((a, b) => a + b, 0);
        return { labels, values, total };
      }

      if (totalDays <= 120) {
        const days = eachDayOfInterval({ start, end });
        const weekMap = new Map<string, number>();

        days.forEach(day => {
          const bucketStart = startOfWeek(day, { weekStartsOn: 1 });
          const key = format(bucketStart, 'dd.MM.yyyy');

          if (!weekMap.has(key)) {
            weekMap.set(key, weekMap.size);
            labels.push(`${format(bucketStart, 'dd.MM')} нед.`);
            values.push(0);
          }
        });

        ops.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;

          const bucketStart = startOfWeek(d, { weekStartsOn: 1 });
          const key = format(bucketStart, 'dd.MM.yyyy');
          const idx = weekMap.get(key);

          if (idx !== undefined) values[idx] += Number(o.amount);
        });

        const total = values.reduce((a, b) => a + b, 0);
        return { labels, values, total };
      }

      const months = eachMonthOfInterval({ start, end });

      labels = months.map(m => format(m, 'LLLL yyyy', { locale: ru }));
      values = new Array(months.length).fill(0);

      ops.forEach(o => {
        const d = new Date(o.date);
        if (!isWithinInterval(d, { start, end })) return;

        const idx = months.findIndex(
          m => m.getFullYear() === d.getFullYear() && m.getMonth() === d.getMonth(),
        );

        if (idx >= 0) values[idx] += Number(o.amount);
      });

      const total = values.reduce((a, b) => a + b, 0);
      return { labels, values, total };
    }

    return { labels: [], values: [], total: 0 };
  }, [ops, period, now, currentYear, currentMonth, today, from, to]);

  const hasData = computed.values.some(v => v > 0);

  const chartData: ChartData<'bar'> = useMemo(
    () => ({
      labels: computed.labels,
      datasets: [
        {
          label: `${title ?? 'Тег'} (100%)`,
          data: computed.values,
          backgroundColor: type === 'income' ? 'rgba(28,172,0,0.5)' : 'rgba(255,0,0,0.5)',
        },
      ],
    }),
    [computed.labels, computed.values, type, title],
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: title ?? 'Диаграмма по тегу' },
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const v = Number(ctx.parsed.y || 0);
              const totalTag = computed.total;
              const pTag = pct(v, totalTag);
              const pAll = pTag;

              return `${v} ₽ (${pTag}% от тега, ${pAll}% от общего)`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text:
              period === 'years'
                ? 'Год'
                : period === 'year'
                  ? 'Месяц'
                  : period === 'month'
                    ? 'Неделя'
                    : period === 'week'
                      ? 'День недели'
                      : 'Выбранный период',
          },
        },
        y: {
          title: { display: true, text: 'Сумма, ₽' },
          beginAtZero: true,
        },
      },
    }),
    [period, title, computed.total],
  );

  const incomeDataForBlock = type === 'income' ? computed.values : [];
  const expenseDataForBlock = type === 'expense' ? computed.values : [];

  return (
    <div>
      <BlockCounted type={type} incomeData={incomeDataForBlock} expenseData={expenseDataForBlock} />

      {hasData ? (
        <Box sx={{ width: '100%', height: { xs: 320, sm: 420, md: 520 } }}>
          <Bar options={options} data={chartData} />
        </Box>
      ) : (
        <div>Нет записей для построения графики</div>
      )}
    </div>
  );
};
